import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import TextField from "@mui/material/TextField";
import {
  ExternalIcon,
  OU,
  OUTREACH_SANS,
  OuBone,
  OuCard,
  OuCardHeader,
  OuControl,
  OuEmptyState,
  OuNotice,
  OuPage,
  OuPill,
  OuSecondaryButton,
  OuSelect,
  OuSwitch,
  OuTone,
  PhoneIcon,
  RefreshIcon,
} from "../CrmUi";
import { getCallerIdHealth, recordCallerIdLabel, setCallerIdRegistered } from "../../services/firebase";
import { CallerIdHealth, CallerIdRecord } from "../../types/addons";
import { useRecoilValue } from "recoil";
import { organizationAtom } from "../../state/atoms";
import { orgPlanLocks } from "../../common/plans";
import PlanUpgradePrompt from "../Billing/PlanUpgradePrompt";

const NumberList = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const NumberBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-top: 1px solid ${OU.rule};

  &:last-of-type {
    padding-bottom: 0;
  }
`;

const NumberHead = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const PhoneNumber = styled("div")`
  font: 600 16px/24px ${OUTREACH_SANS};
  font-variant-numeric: tabular-nums;
  color: ${OU.ink};
`;

const Meta = styled("p")`
  margin: 0;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const LabelForm = styled("div")`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);

    > *:last-child {
      justify-self: end;
    }
  }
`;

const FixBox = styled("div")`
  border-radius: 8px;
  background: ${OU.wash};
  padding: 16px;
`;

const FixTitle = styled("div")`
  font: 600 14px/20px ${OUTREACH_SANS};
  color: ${OU.ink};
`;

const Registry = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid ${OU.rule};

  &:first-of-type {
    margin-top: 12px;
  }
`;

const MoreLinks = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 10px;
  border-top: 1px solid ${OU.rule};
  font: 400 13px/18px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const STATUS_TONE: Record<CallerIdRecord["status"], OuTone> = {
  healthy: "success",
  watch: "warning",
  degraded: "caution",
  flagged: "danger",
};

const CARRIERS = [
  { value: "verizon", label: "Verizon" },
  { value: "att", label: "AT&T" },
  { value: "tmobile", label: "T-Mobile" },
];
const pct = (v: number | null) => (v == null ? "n/a" : `${Math.round(v * 100)}%`);

const Ext: FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
    <ExternalIcon />
  </a>
);

/**
 * The caller ID page while its health report is still being fetched: the
 * health card with its refresh button on the right, then the numbers card,
 * each number a heading row, its metrics line and the label form.
 */
export const CallerIdTabSkeleton: FC = () => (
  <OuPage aria-busy="true">
    <OuCard>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24 }}>
        <div style={{ flex: "1 1 auto" }}>
          <OuBone variant="text" width={180} sx={{ fontSize: "1.25rem" }} />
          <OuBone variant="text" width="92%" />
          <OuBone variant="text" width="64%" />
        </div>
        <OuBone variant="rounded" width={132} height={36} sx={{ borderRadius: "100px" }} />
      </div>
    </OuCard>

    <OuCard>
      <OuBone variant="text" width={80} sx={{ fontSize: "1rem" }} />
      <NumberList>
        {[0, 1, 2].map((i) => (
          <NumberBlock key={i}>
            <NumberHead>
              <OuBone variant="text" width={148} sx={{ fontSize: "1rem" }} />
              <OuBone variant="rounded" width={112} height={24} sx={{ borderRadius: "100px" }} />
            </NumberHead>
            <div>
              <OuBone variant="text" width="88%" />
              <OuBone variant="text" width="56%" />
            </div>
            <LabelForm>
              <OuBone variant="rounded" height={40} sx={{ borderRadius: "8px" }} />
              <OuBone variant="rounded" height={40} sx={{ borderRadius: "8px" }} />
              <OuBone variant="rounded" width={124} height={36} sx={{ borderRadius: "100px" }} />
            </LabelForm>
          </NumberBlock>
        ))}
      </NumberList>
    </OuCard>
  </OuPage>
);

/** Caller ID health for every number the team dials from in Outreach, with labels and remediation. */
const CallerIdTab: FC = () => {
  const organization = useRecoilValue(organizationAtom);
  const [health, setHealth] = useState<CallerIdHealth | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState<Record<string, { carrier: string; label: string }>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh = false) => {
    setBusy(refresh ? "refresh" : null);
    try {
      setHealth(await getCallerIdHealth(refresh));
    } catch (e: any) {
      setError(e?.message || "Could not load caller ID health.");
    } finally {
      setBusy(null);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!health && error) {
    return (
      <OuPage>
        <OuCard>
          <OuCardHeader level="page" title="Caller ID health" />
          <OuNotice tone="danger">{error}</OuNotice>
        </OuCard>
      </OuPage>
    );
  }

  if (!health) return <CallerIdTabSkeleton />;

  const saveLabel = async (n: CallerIdRecord) => {
    const d = labelDraft[n.number] || { carrier: "verizon", label: "" };
    setBusy(n.number);
    try {
      const numbers = await recordCallerIdLabel(n.number, d.carrier, d.label || null);
      setHealth({ ...health, numbers });
    } finally {
      setBusy(null);
    }
  };
  const toggleRegistered = async (n: CallerIdRecord, registry: "fcr" | "hiya", on: boolean) => {
    setBusy(`${n.number}:${registry}`);
    try {
      setHealth({ ...health, numbers: await setCallerIdRegistered(n.number, registry, on) });
    } finally {
      setBusy(null);
    }
  };

  const flagged = health.numbers.filter((n) => n.status === "flagged" || n.status === "degraded").length;

  return (
    <OuPage>
      <OuCard>
        <OuCardHeader
          level="page"
          title="Caller ID health"
          description={
            <>
              Scored from your team&apos;s Outreach calls: answer rate against each number&apos;s own history and the rest of the team, shortening connected calls, and volume.
              {health.numbers.length > 0 ? ` ${health.numbers.length} numbers, ${flagged} need attention.` : ""}
            </>
          }
          aside={
            <OuSecondaryButton
              type="button"
              icon={<RefreshIcon />}
              disabled={!health.enabled || busy === "refresh"}
              loading={busy === "refresh"}
              onClick={() => load(true)}
            >
              Refresh now
            </OuSecondaryButton>
          }
        />
        {!health.hasDialer &&
          (orgPlanLocks(organization, "dialer") ? (
            <PlanUpgradePrompt feature="dialer" what="Dial faster and caller ID monitoring" />
          ) : (
            <OuNotice tone="info">Caller ID monitoring comes with the Dialer add-on. Subscribe under Settings, Add-ons.</OuNotice>
          ))}
        {health.hasDialer && health.needsScopes && <OuNotice tone="warning">Reconnect Outreach under Settings, Integration, so boring can read call records.</OuNotice>}
      </OuCard>

      {health.numbers.length === 0 && health.enabled && (
        <OuCard>
          <OuCardHeader title="Numbers" />
          <OuEmptyState icon={<PhoneIcon />} title="No calls found yet" hint="Numbers appear after the first daily sweep or a refresh." />
        </OuCard>
      )}

      {health.numbers.length > 0 && (
        <OuCard>
          <OuCardHeader title="Numbers" />
          <NumberList>
            {health.numbers.map((n) => {
              const draft = labelDraft[n.number] || { carrier: "verizon", label: "" };
              return (
                <NumberBlock key={n.number}>
                  <NumberHead>
                    <PhoneNumber>{n.number}</PhoneNumber>
                    <OuPill tone={STATUS_TONE[n.status] || "neutral"}>
                      {n.status}
                      {n.score != null ? `, score ${n.score}` : ""}
                    </OuPill>
                  </NumberHead>
                  <Meta>
                    {n.users.length ? `Used by ${n.users.join(", ")}. ` : ""}
                    {n.metrics.recent_calls} calls in the last 48 hours, {pct(n.metrics.recent_answer_rate)} answered
                    {n.metrics.baseline_answer_rate != null ? ` (baseline ${pct(n.metrics.baseline_answer_rate)})` : ""}, {n.metrics.daily_volume_7d} a day this week.
                    {n.metrics.reasons.length ? ` ${n.metrics.reasons.join(". ")}.` : ""}
                  </Meta>
                  {n.labels.length > 0 && <Meta>Labels recorded: {n.labels.map((l) => `${l.carrier}: ${l.label || "clean"}`).join(", ")}</Meta>}
                  <LabelForm>
                    <OuControl>
                      <OuSelect
                        aria-label={`Carrier for ${n.number}`}
                        value={draft.carrier}
                        onChange={(v) => setLabelDraft({ ...labelDraft, [n.number]: { ...draft, carrier: v } })}
                        options={CARRIERS}
                      />
                    </OuControl>
                    <OuControl>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="What the handset showed, for example Spam Likely"
                        inputProps={{ "aria-label": `Label shown for ${n.number}` }}
                        value={draft.label}
                        onChange={(e) => setLabelDraft({ ...labelDraft, [n.number]: { ...draft, label: e.target.value } })}
                      />
                    </OuControl>
                    <OuSecondaryButton type="button" disabled={busy === n.number} loading={busy === n.number} onClick={() => saveLabel(n)}>
                      Record label
                    </OuSecondaryButton>
                  </LabelForm>
                  {(n.status === "flagged" || n.status === "degraded") && (
                    <FixBox>
                      <FixTitle>Fix it</FixTitle>
                      <Meta>Register the number with the carriers&apos; analytics engines, then let it rest a few days. Mark each step when done.</Meta>
                      <Registry>
                        <Ext href={health.links.freeCallerRegistry}>Free Caller Registry</Ext>
                        <OuSwitch
                          aria-label={`Registered ${n.number} with Free Caller Registry`}
                          checked={!!n.registeredFcrAt}
                          saving={busy === `${n.number}:fcr`}
                          onChange={(on) => toggleRegistered(n, "fcr", on)}
                        />
                      </Registry>
                      <Registry>
                        <Ext href={health.links.hiya}>Hiya</Ext>
                        <OuSwitch
                          aria-label={`Registered ${n.number} with Hiya`}
                          checked={!!n.registeredHiyaAt}
                          saving={busy === `${n.number}:hiya`}
                          onChange={(on) => toggleRegistered(n, "hiya", on)}
                        />
                      </Registry>
                      <MoreLinks>
                        <Ext href={health.links.tns}>TNS</Ext>
                        <Ext href={health.links.firstOrion}>First Orion</Ext>
                      </MoreLinks>
                    </FixBox>
                  )}
                </NumberBlock>
              );
            })}
          </NumberList>
        </OuCard>
      )}
    </OuPage>
  );
};

export default CallerIdTab;
